import { Component, computed, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { OrgChartStore } from './org-chart.state';

/** A labelled button in the panels at the chart's foot. */
export const ORG_CHART_TOOLBAR_BUTTON_CLASS =
  'flex h-7 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40 aria-pressed:bg-accent aria-pressed:text-foreground';

/**
 * Expand / collapse panel beside the zoom controls.
 *
 * Every OE card starts with its Stellen and stations closed, so the first view
 * is the structure alone. "Stellen" opens every card's list of Stellen, "Alle"
 * also every person list and every list of stations; each closes again once it
 * is fully applied.
 */
@Component({
  selector: 'org-chart-expand-controls',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div
      class="flex items-center gap-0.5 rounded-lg border border-border bg-card p-1 shadow-sm"
    >
      <button
        type="button"
        [class]="buttonClass"
        [disabled]="!hasPositions()"
        [attr.aria-pressed]="store.allPositionListsExpanded()"
        [title]="
          (store.allPositionListsExpanded()
            ? 'organigram.toolbar.collapsePositions'
            : 'organigram.toolbar.expandPositions'
          ) | transloco
        "
        (click)="togglePositionLists()"
      >
        <span class="material-icons text-base" aria-hidden="true"
          >view_agenda</span
        >
        {{ 'organigram.toolbar.positions' | transloco }}
      </button>

      <button
        type="button"
        [class]="buttonClass"
        [disabled]="!hasAnything()"
        [attr.aria-pressed]="store.everythingExpanded()"
        [title]="
          (store.everythingExpanded()
            ? 'organigram.toolbar.collapseAll'
            : 'organigram.toolbar.expandAll'
          ) | transloco
        "
        (click)="toggleAll()"
      >
        <span class="material-icons text-base" aria-hidden="true">{{
          store.everythingExpanded() ? 'unfold_less' : 'unfold_more'
        }}</span>
        {{ 'organigram.toolbar.all' | transloco }}
      </button>
    </div>
  `,
})
export class OrgChartExpandControlsComponent {
  readonly store = inject(OrgChartStore);

  readonly buttonClass = ORG_CHART_TOOLBAR_BUTTON_CLASS;
  readonly hasPositions = computed(
    () => this.store.expandable().oeIds.length > 0,
  );
  readonly hasAnything = computed(
    () =>
      this.hasPositions() ||
      this.store.expandable().stationListOeIds.length > 0,
  );

  togglePositionLists(): void {
    if (this.store.allPositionListsExpanded()) {
      this.store.collapseAllPositionLists();
    } else {
      this.store.expandAllPositionLists();
    }
  }

  toggleAll(): void {
    if (this.store.everythingExpanded()) {
      this.store.collapseAll();
    } else {
      this.store.expandAll();
    }
  }
}
